"use client";

import { useEffect, useState, useCallback } from "react";
import { useParams, useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { BottomNav } from "@/components/bottom-nav";
import {
  ArrowLeft,
  CheckCircle2,
  Phone,
  Mail,
  MessageSquare,
  Loader2,
  AlertCircle,
  MapPin,
  Calendar,
  ShieldAlert,
  Star,
  RotateCcw,
} from "lucide-react";
import toast from "react-hot-toast";

interface HiredWorkerDetails {
  matchId: string;
  hiredAt: string | null;
  noShow: boolean;
  jobTitle: string;
  jobStartDate: string | null;
  workerId: string;
  worker: {
    name: string | null;
    phone: string | null;
    email: string | null;
    suburb: string | null;
    avatarUrl: string | null;
    trades: string[];
    experienceYears: number | null;
  };
}

export default function ContractorHiredDetailPage() {
  const params = useParams();
  const router = useRouter();
  const matchId = params.matchId as string;
  const supabase = createClient();
  const [details, setDetails] = useState<HiredWorkerDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startingChat, setStartingChat] = useState(false);
  const [reportingNoShow, setReportingNoShow] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [savingFav, setSavingFav] = useState(false);

  const loadDetails = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { router.push("/auth/login"); return; }

      // Fetch match with worker + job details
      const { data: match, error: matchError } = await supabase
        .from("matches")
        .select(`
          id, status, hired_at, worker_id, no_show,
          jobs!inner(title, start_date, company_id, companies!inner(profile_id))
        `)
        .eq("id", matchId)
        .eq("status", "hired")
        .single();

      if (matchError || !match) {
        setError("Hire not found.");
        setLoading(false);
        return;
      }

      // Verify contractor owns this job
      const jobs = match.jobs as unknown as { title: string; start_date: string | null; company_id: string; companies: { profile_id: string } };
      if (jobs.companies.profile_id !== user.id) {
        setError("You don't have access to this hire.");
        setLoading(false);
        return;
      }

      // Get worker contact details
      const [{ data: workerProfile }, { data: workerDetails }] = await Promise.all([
        supabase.from("profiles").select("name, phone, email, suburb, avatar_url").eq("id", match.worker_id).single(),
        supabase.from("worker_profiles").select("trades, experience_years").eq("profile_id", match.worker_id).single(),
      ]);

      // Check if worker is favorited
      const { data: favData } = await supabase
        .from('worker_favorites')
        .select('id')
        .eq('contractor_id', user.id)
        .eq('worker_id', match.worker_id)
        .limit(1);
      setIsFavorite((favData?.length || 0) > 0);

      setDetails({
        matchId: match.id,
        hiredAt: match.hired_at,
        noShow: match.no_show || false,
        jobTitle: jobs.title,
        jobStartDate: jobs.start_date,
        workerId: match.worker_id,
        worker: {
          name: workerProfile?.name || null,
          phone: workerProfile?.phone || null,
          email: workerProfile?.email || null,
          suburb: workerProfile?.suburb || null,
          avatarUrl: workerProfile?.avatar_url || null,
          trades: workerDetails?.trades || [],
          experienceYears: workerDetails?.experience_years || null,
        },
      });
      setLoading(false);
    } catch (err) {
      logger.error("Error loading hired worker:", err);
      setError("Failed to load details.");
      setLoading(false);
    }
  }, [matchId, supabase, router]);

  useEffect(() => { loadDetails(); }, [loadDetails]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="mx-auto max-w-screen-sm px-4 py-6">
          <Button variant="ghost" onClick={() => router.back()} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <div className="flex flex-col items-center py-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{error}</p>
            <Button onClick={() => { setLoading(true); setError(null); loadDetails(); }} className="mt-4">Try Again</Button>
          </div>
        </div>
        <BottomNav userType="contractor" />
      </div>
    );
  }

  const workerName = details.worker.name || "Worker";
  const initial = workerName[0]?.toUpperCase() || "?";

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="mx-auto max-w-screen-sm px-4 py-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => router.back()}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Hired Worker</h1>
        </div>

        {/* Worker identity - big and clear */}
        <div className="text-center mb-6">
          {details.worker.avatarUrl ? (
            <img src={details.worker.avatarUrl} alt={workerName} className="h-24 w-24 rounded-full object-cover mx-auto mb-3" />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-4xl mx-auto mb-3">
              {initial}
            </div>
          )}
          <h2 className="text-2xl font-bold">{workerName}</h2>
          <div className="flex items-center justify-center gap-2 mt-1 text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span>Hired for <span className="font-medium text-foreground">{details.jobTitle}</span></span>
          </div>
          {details.hiredAt && (
            <p className="text-xs text-muted-foreground mt-1">
              {new Date(details.hiredAt).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          )}
          <div className="flex items-center justify-center gap-3 mt-2 text-sm text-muted-foreground">
            {details.worker.suburb && (
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {details.worker.suburb}</span>
            )}
            {details.worker.experienceYears && (
              <span>{details.worker.experienceYears}yr exp</span>
            )}
          </div>
        </div>

        {/* === THE STUPID SIMPLE CONTACT BUTTONS === */}
        <div className="space-y-4">

          {/* CALL — biggest, most prominent */}
          {details.worker.phone && (
            <a href={`tel:${details.worker.phone}`} className="block">
              <div className="flex items-center gap-4 rounded-2xl bg-green-600 p-5 text-white active:scale-[0.97] transition-transform shadow-lg">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
                  <Phone className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-xl font-bold">Call {workerName.split(" ")[0]}</p>
                  <p className="text-lg text-white/80">{details.worker.phone}</p>
                </div>
              </div>
            </a>
          )}

          {/* SMS */}
          {details.worker.phone && (
            <a href={`sms:${details.worker.phone}`} className="block">
              <div className="flex items-center gap-4 rounded-2xl bg-blue-600 p-5 text-white active:scale-[0.97] transition-transform shadow-lg">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
                  <MessageSquare className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-xl font-bold">Text {workerName.split(" ")[0]}</p>
                  <p className="text-lg text-white/80">{details.worker.phone}</p>
                </div>
              </div>
            </a>
          )}

          {/* EMAIL */}
          {details.worker.email && (
            <a href={`mailto:${details.worker.email}`} className="block">
              <div className="flex items-center gap-4 rounded-2xl bg-slate-700 p-5 text-white active:scale-[0.97] transition-transform shadow-lg">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
                  <Mail className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-xl font-bold">Email {workerName.split(" ")[0]}</p>
                  <p className="text-lg text-white/80 truncate">{details.worker.email}</p>
                </div>
              </div>
            </a>
          )}

          {/* No contact info fallback */}
          {!details.worker.phone && !details.worker.email && (
            <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-5 text-center">
              <AlertCircle className="h-8 w-8 text-amber-600 mx-auto mb-2" />
              <p className="text-base font-medium text-amber-900">No contact details available yet</p>
              <p className="text-sm text-amber-700 mt-1">Send a message through the app below.</p>
            </div>
          )}

          {/* In-app message — opens/creates conversation directly */}
          <button
            onClick={async () => {
              if (!details?.workerId) return;
              setStartingChat(true);
              try {
                // Get CSRF token
                const csrfRes = await fetch("/api/auth/csrf");
                const { token: csrfToken } = await csrfRes.json();
                
                const res = await fetch("/api/messages/start", {
                  method: "POST",
                  headers: { "Content-Type": "application/json", "x-csrf-token": csrfToken },
                  body: JSON.stringify({ recipientId: details.workerId }),
                });
                if (res.ok) {
                  const data = await res.json();
                  router.push(`/messages/${data.conversationId}`);
                } else {
                  // Fallback to messages list
                  router.push("/messages");
                }
              } catch {
                router.push("/messages");
              } finally {
                setStartingChat(false);
              }
            }}
            disabled={startingChat}
            className="block w-full text-left"
          >
            <div className="flex items-center gap-4 rounded-2xl border-2 border-primary p-5 active:scale-[0.97] transition-transform">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                {startingChat ? (
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                ) : (
                  <MessageSquare className="h-8 w-8 text-primary" />
                )}
              </div>
              <div>
                <p className="text-xl font-bold">In-App Message</p>
                <p className="text-base text-muted-foreground">Chat within RateRight</p>
              </div>
            </div>
          </button>
        </div>

        {/* Rehire & Favorite Actions */}
        <div className="mt-8 space-y-3">
          {/* Hire Again */}
          <Button
            className="w-full h-14 text-lg font-semibold gap-2"
            onClick={() => router.push(`/contractor/post-job?rehire=${details.workerId}&name=${encodeURIComponent(workerName)}`)}
          >
            <RotateCcw className="h-5 w-5" />
            Hire {workerName.split(" ")[0]} Again
          </Button>

          {/* Save to Favorites */}
          <Button
            variant={isFavorite ? "secondary" : "outline"}
            className="w-full h-12 text-base gap-2"
            disabled={savingFav}
            onClick={async () => {
              setSavingFav(true);
              try {
                if (isFavorite) {
                  await fetch('/api/favorites', {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ worker_id: details.workerId }),
                  });
                  setIsFavorite(false);
                  toast.success('Removed from favorites');
                } else {
                  await fetch('/api/favorites', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ worker_id: details.workerId }),
                  });
                  setIsFavorite(true);
                  toast.success('Saved to favorites');
                }
              } catch {
                toast.error('Failed to update favorites');
              } finally {
                setSavingFav(false);
              }
            }}
          >
            <Star className={`h-5 w-5 ${isFavorite ? 'fill-yellow-500 text-yellow-500' : ''}`} />
            {isFavorite ? 'Saved to Favorites' : 'Save to Favorites'}
          </Button>
        </div>

        {/* No-Show Report Section */}
        <div className="mt-10 pt-6 border-t border-border">
          {details.noShow ? (
            <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-center">
              <ShieldAlert className="h-6 w-6 text-red-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-red-800">No-show reported</p>
              <p className="text-xs text-red-600 mt-1">This worker has been flagged for review.</p>
            </div>
          ) : (
            <button
              onClick={async () => {
                if (!confirm(`Report ${workerName} as a no-show for "${details.jobTitle}"? This will flag their profile for quality review.`)) {
                  return;
                }
                setReportingNoShow(true);
                try {
                  const csrfRes = await fetch("/api/auth/csrf");
                  const { token: csrfToken } = await csrfRes.json();

                  const res = await fetch("/api/match/no-show", {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "x-csrf-token": csrfToken },
                    body: JSON.stringify({ matchId: details.matchId }),
                  });
                  const data = await res.json();
                  if (res.ok) {
                    toast.success("No-show reported. Worker has been notified.");
                    setDetails((prev) => prev ? { ...prev, noShow: true } : prev);
                  } else {
                    toast.error(data.error || "Failed to report no-show");
                  }
                } catch {
                  toast.error("Something went wrong. Try again.");
                } finally {
                  setReportingNoShow(false);
                }
              }}
              disabled={reportingNoShow}
              className="w-full rounded-xl border-2 border-red-200 p-4 text-center hover:bg-red-50 transition-colors"
            >
              <div className="flex items-center justify-center gap-2 text-red-600">
                {reportingNoShow ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <ShieldAlert className="h-5 w-5" />
                )}
                <span className="font-medium">Report No-Show</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Worker didn&apos;t show up? Report after the 48-hour grace period.
              </p>
            </button>
          )}

          <button
            onClick={() => router.push(`/report-issue?matchId=${details.matchId}&role=contractor`)}
            className="w-full rounded-xl border border-border bg-card p-4 text-center transition-colors hover:bg-accent mt-4"
          >
            <div className="flex items-center justify-center gap-2 text-foreground">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Report an Issue</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Have a problem with this match? Let us know.
            </p>
          </button>
        </div>
      </div>
      <BottomNav userType="contractor" />
    </div>
  );
}
